import { useQuery } from '@tanstack/react-query';
import {
  getMedewerkers,
  getSignaleringen,
  getWeekrapport,
  type MedewerkersAntwoord,
  type SignaleringenAntwoord,
  type WeekrapportAntwoord,
} from '../work-quality.api';

export interface Lezing<T> {
  data?: T;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * De drie antwoorden waar Alle medewerkers uit bestaat, elk in zijn eigen aanroep.
 *
 * Drie aanroepen en niet één: dat is hoe de dienst het aanbiedt (`/medewerkers`, `/signaleringen`,
 * `/weekrapport`), en het is precies de reden dat AD-24 bestaat — een beheerder kan de drempel
 * verzetten terwijl de drie onderweg zijn. De pagina legt de versienummers daarom naast elkaar en
 * herlaadt in plaats van twee lezingen te mengen.
 *
 * Geen `refetchInterval` en geen verversing bij vensterfocus, net als bij de eigen week: een
 * meetwaarde die vanzelf verspringt suggereert beweging waar meting hoort.
 */
function useLezing<T>(sleutel: string, week: string, ophalen: () => Promise<T>): Lezing<T> {
  const query = useQuery({
    queryKey: ['workQuality', sleutel, week],
    queryFn: ophalen,
    enabled: week !== '',
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
  };
}

export function useMedewerkers(week: string): Lezing<MedewerkersAntwoord> {
  return useLezing('employees', week, () => getMedewerkers(week));
}

export function useSignaleringen(week: string): Lezing<SignaleringenAntwoord> {
  return useLezing('signals', week, () => getSignaleringen(week));
}

export function useWeekrapport(week: string): Lezing<WeekrapportAntwoord> {
  return useLezing('week-report', week, () => getWeekrapport(week));
}
